import { Anton, Archivo, DM_Sans } from 'next/font/google'

/**
 * MTA type system — exactly two families, no more.
 *
 * Anton is the display voice: condensed, single-weight, uppercase, used only
 * for short headline text. DM Sans is the functional voice: everything a
 * visitor actually reads or operates.
 *
 * Both are loaded through `next/font`, so they are self-hosted, preloaded and
 * given a metric-matched fallback. Never add an `@import url(...)` for a font
 * to `globals.css`, and never add a third family.
 */

/**
 * Display. Anton ships in a single weight (400) that already reads as heavy,
 * so never ask for a bold: the browser would synthesise a smeared faux-bold.
 * Emphasis comes from SIZE, not weight.
 */
export const anton = Anton({
    weight: '400',
    subsets: ['latin'],
    display: 'swap'
})

/**
 * Functional. Variable weight, so 400 through 800 are all real cuts.
 * The 'latin' subset covers Latin-1 Supplement, which is where the Albanian
 * ë / Ë / ç / Ç live, so Albanian copy renders from the real face rather
 * than a fallback.
 */
export const dmSans = DM_Sans({
    subsets: ['latin'],
    display: 'swap'
})

/**
 * Poster. The wide, heavy grotesk of the approved home artwork, used by the
 * hero headline (BUY. / SELL. / CONNECT.), its sub-headline, the hero nav and
 * the hero's button labels. 800 and 900 are real cuts, never synthesised.
 * Do not reach for this outside the hero.
 */
export const archivo = Archivo({
    weight: ['800', '900'],
    subsets: ['latin'],
    display: 'swap'
})

/**
 * Declared at `:root` rather than on a wrapper element, so the tokens still
 * resolve inside overlays that portal to `document.body` (dialog, dropdown,
 * select, tooltip). `globals.css` maps these onto the Tailwind font tokens
 * inside `@theme inline` — change the faces here and the CSS needs no edit.
 */
export const fontRootVariables = [
    ':root{',
    `--font-app-sans:${dmSans.style.fontFamily};`,
    `--font-app-display:${anton.style.fontFamily};`,
    `--font-app-heading:${dmSans.style.fontFamily};`,
    `--font-app-poster:${archivo.style.fontFamily}`,
    '}'
].join('')
